<!--
  SubscriptionCompCard -- the SA "Subscription comp" section of the
  distributor page (DistributorEdit.vue).

  Design: docs/planning/flat-fee-and-allowance-billing-2026-10-20.md section
  4.4 (Jake, 2026-10-20). Cards: "#384: Billing: price plan tiers with any
  combination of flat, active, dormant, custom-rate and included-store fees"
  (https://trello.com/c/5wMcO5Bg) and "Per-store subscription billing"
  (https://trello.com/c/WJxkNmTo).

  Shows the current state ("Comped since <date>: <reason>" or "Not
  comped"), the charges a comp covers (the flat fee and both per-store
  fees), a Comp / End comp action that REQUIRES a reason (1-500 characters),
  and the history. Its own explicit actions, not part of the page's
  auto-saving profile form: a comp takes effect the moment it is confirmed.

  The wire is /api/superadmin/distributors/:id/comp|uncomp, which call the
  same comps.py function `flask comp-distributors` calls, so this section and
  the command cannot disagree.
-->
<template>
    <section class="sub-comp" data-testid="sa-comp-card">
        <h2 class="sub-comp__title">Subscription comp</h2>

        <p v-if="loading" data-testid="sa-comp-loading">Loading comp status...</p>

        <p
            v-else-if="loadError"
            class="sub-comp__error"
            data-testid="sa-comp-load-error"
        >
            {{ loadError }}
        </p>

        <template v-else>
            <p class="sub-comp__state" data-testid="sa-comp-state">
                <template v-if="state.comped">
                    Comped since {{ formatDate(state.since) }}: {{ state.reason }}
                </template>
                <template v-else>Not comped</template>
            </p>

            <ul class="sub-comp__charges" data-testid="sa-comp-charges">
                <li
                    v-for="c in state.charges"
                    :key="c.charge_id"
                    :data-testid="`sa-comp-charge-${c.charge_id}`"
                >
                    {{ c.label }} ({{ dollars(c.amount_cents) }})
                    <span v-if="c.comped" class="sub-comp__comped"> - comped</span>
                </li>
            </ul>

            <p class="sub-comp__timing" data-testid="sa-comp-timing">
                A comp prices every subscription charge at $0 for each month
                still to end, including the current month in full. Months that
                have already ended keep their price. Ending a comp bills the
                current month in full.
            </p>

            <form
                v-if="!state.comped"
                class="sub-comp__form"
                data-testid="sa-comp-form"
                @submit.prevent="askComp"
            >
                <label class="sub-comp__label" for="sa-comp-reason">
                    Reason (required)
                </label>
                <input
                    id="sa-comp-reason"
                    v-model="compReason"
                    type="text"
                    maxlength="500"
                    class="sub-comp__input"
                    data-testid="sa-comp-reason"
                />
                <button
                    type="button"
                    class="sub-comp__btn"
                    data-testid="sa-comp-submit"
                    @click="askComp"
                    :disabled="!compReasonValid || saving"
                >
                    Comp
                </button>
            </form>

            <form
                v-else
                class="sub-comp__form"
                data-testid="sa-uncomp-form"
                @submit.prevent="askUncomp"
            >
                <label class="sub-comp__label" for="sa-uncomp-reason">
                    Reason for ending the comp (required)
                </label>
                <input
                    id="sa-uncomp-reason"
                    v-model="uncompReason"
                    type="text"
                    maxlength="500"
                    class="sub-comp__input"
                    data-testid="sa-uncomp-reason"
                />
                <button
                    type="button"
                    class="sub-comp__btn"
                    data-testid="sa-uncomp-submit"
                    @click="askUncomp"
                    :disabled="!uncompReasonValid || saving"
                >
                    End comp
                </button>
            </form>

            <!--
              Explicit confirmation before either write: a comp changes what
              the distributor is billed, so one stray click must not do it.
              Cancel keeps the typed reason.
            -->
            <div
                v-if="confirming === 'comp'"
                class="sub-comp__confirm"
                role="alertdialog"
                data-testid="sa-comp-confirm"
            >
                <p>
                    Comp this distributor? Every subscription charge is priced
                    at $0 for each month that has not ended. Reason:
                    {{ compReason.trim() }}
                </p>
                <button
                    type="button"
                    class="sub-comp__btn"
                    data-testid="sa-comp-confirm-submit"
                    :disabled="saving"
                    @click="onComp"
                >
                    Confirm comp
                </button>
                <button
                    type="button"
                    class="sub-comp__btn"
                    data-testid="sa-comp-confirm-cancel"
                    @click="confirming = null"
                >
                    Cancel
                </button>
            </div>
            <div
                v-if="confirming === 'uncomp'"
                class="sub-comp__confirm"
                role="alertdialog"
                data-testid="sa-uncomp-confirm"
            >
                <p>
                    End this comp? The current month is billed in full again.
                    Reason: {{ uncompReason.trim() }}
                </p>
                <button
                    type="button"
                    class="sub-comp__btn"
                    data-testid="sa-uncomp-confirm-submit"
                    :disabled="saving"
                    @click="onUncomp"
                >
                    Confirm end comp
                </button>
                <button
                    type="button"
                    class="sub-comp__btn"
                    data-testid="sa-uncomp-confirm-cancel"
                    @click="confirming = null"
                >
                    Cancel
                </button>
            </div>

            <p
                v-if="saveError"
                class="sub-comp__error"
                data-testid="sa-comp-error"
            >
                {{ saveError }}
            </p>

            <div v-if="state.history.length" data-testid="sa-comp-history">
                <h3 class="sub-comp__subtitle">History</h3>
                <table class="sub-comp__table">
                    <thead>
                        <tr>
                            <th>Comped</th>
                            <th>By</th>
                            <th>Reason</th>
                            <th>Covered</th>
                            <th>Ended</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(h, i) in state.history"
                            :key="`${h.comped_at}-${i}`"
                            :data-testid="`sa-comp-history-row-${i}`"
                        >
                            <td>{{ formatDate(h.comped_at) }}</td>
                            <td>{{ h.comped_by }}</td>
                            <td>{{ h.reason }}</td>
                            <td>{{ h.charges.map((c) => c.label).join(", ") }}</td>
                            <td>
                                <template v-if="h.active">Still comped</template>
                                <template v-else>
                                    {{ formatDate(h.ended_at) }}
                                    <template v-if="h.ended_by"> by {{ h.ended_by }}</template>
                                    <template v-if="h.end_reason">: {{ h.end_reason }}</template>
                                </template>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!--
              Custom rate / included-store override: same record (one GET), so
              a comp and a rate cannot disagree on screen. Each write hands the
              server's new state back up.
            -->
            <SubscriptionTermsSection
                :distributor-id="distributorId"
                :state="state"
                @updated="onTermsUpdated"
            />
        </template>
    </section>
</template>

<script>
import { getDistributorComp, compDistributor, uncompDistributor } from "@/services/superadminPricingService";
import SubscriptionTermsSection from "@/pages/superadmin/SubscriptionTermsSection.vue";

const REASON_MAX = 500;

function serverMessage(e, fallback) {
    return (e && e.response && e.response.data && e.response.data.message) || fallback;
}

const EMPTY = { comped: false, since: null, reason: null, charges: [], history: [] };

export default {
    name: "SubscriptionCompCard",

    components: { SubscriptionTermsSection },

    props: {
        distributorId: { type: [String, Number], required: true },
    },

    data() {
        return {
            loading: true,
            loadError: "",
            saving: false,
            saveError: "",
            confirming: null,
            state: { ...EMPTY },
            compReason: "",
            uncompReason: "",
        };
    },

    computed: {
        compReasonValid() {
            const n = this.compReason.trim().length;
            return n >= 1 && n <= REASON_MAX;
        },
        uncompReasonValid() {
            const n = this.uncompReason.trim().length;
            return n >= 1 && n <= REASON_MAX;
        },
    },

    watch: {
        distributorId() {
            this.load();
        },
    },

    mounted() {
        this.load();
    },

    methods: {
        async load() {
            this.loading = true;
            this.loadError = "";
            try {
                const resp = await getDistributorComp(this.distributorId);
                this.state = this.normalize(resp && resp.data ? resp.data : resp);
            } catch (e) {
                this.loadError =
                    e && e.response && e.response.status === 404
                        ? "This distributor has no subscription yet. Assign a subscription tier above, then come back to comp it."
                        : serverMessage(e, "Could not load the comp status.");
            } finally {
                this.loading = false;
            }
        },

        onTermsUpdated(data) {
            this.state = this.normalize(data);
        },

        normalize(data) {
            return { ...EMPTY, ...(data || {}), history: (data && data.history) || [], charges: (data && data.charges) || [] };
        },

        // Every path in (button click, Enter in the field, a programmatic form
        // submit) lands here and only OPENS the confirmation; the service is
        // called solely from the Confirm button's handler below.
        askComp() {
            if (this.compReasonValid && !this.saving) this.confirming = "comp";
        },

        askUncomp() {
            if (this.uncompReasonValid && !this.saving) this.confirming = "uncomp";
        },

        async onComp() {
            this.confirming = null;
            if (!this.compReasonValid || this.saving) return;
            await this.send(() => compDistributor(this.distributorId, this.compReason.trim()), "Could not comp this distributor.");
            if (!this.saveError) this.compReason = "";
        },

        async onUncomp() {
            this.confirming = null;
            if (!this.uncompReasonValid || this.saving) return;
            await this.send(() => uncompDistributor(this.distributorId, this.uncompReason.trim()), "Could not end the comp.");
            if (!this.saveError) this.uncompReason = "";
        },

        // One path for both actions: the server answers with the new state, so
        // the section re-renders from the record, never from a local guess.
        async send(call, fallback) {
            this.saving = true;
            this.saveError = "";
            try {
                const resp = await call();
                this.state = this.normalize(resp && resp.data ? resp.data : resp);
            } catch (e) {
                this.saveError = serverMessage(e, fallback);
            } finally {
                this.saving = false;
            }
        },

        dollars(cents) {
            return `$${((cents || 0) / 100).toFixed(2)}`;
        },

        formatDate(iso) {
            if (!iso) return "";
            const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
            return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString();
        },
    },
};
</script>

<style scoped>
.sub-comp {
    margin-top: 24px;
}
.sub-comp__title {
    font-size: 1.1rem;
    margin-bottom: 8px;
}
.sub-comp__subtitle {
    font-size: 1rem;
    margin: 16px 0 8px;
}
.sub-comp__charges {
    margin: 8px 0 8px 20px;
}
.sub-comp__timing {
    font-size: 0.85rem;
    opacity: 0.8;
    margin-bottom: 12px;
}
.sub-comp__form {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}
.sub-comp__input {
    flex: 1 1 280px;
    min-width: 200px;
    border-bottom: 1px solid currentColor;
    padding: 4px 2px;
}
.sub-comp__btn {
    padding: 6px 14px;
    border: 1px solid currentColor;
    border-radius: 4px;
}
.sub-comp__btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}
.sub-comp__confirm {
    margin-top: 12px;
    padding: 8px 12px;
    border: 1px solid currentColor;
    border-radius: 4px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}
.sub-comp__error {
    color: #b00020;
    margin-top: 8px;
}
.sub-comp__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
}
.sub-comp__table th,
.sub-comp__table td {
    text-align: left;
    padding: 4px 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    vertical-align: top;
}
</style>
